import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { vendorAuthService, VendorSession } from '../services/vendorAuthService';
import { toast } from 'react-toastify';

interface VendorAuthContextType {
  vendor: VendorSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  validateToken: (token: string) => Promise<boolean>;
  setPassword: (password: string) => Promise<boolean>;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const VendorAuthContext = createContext<VendorAuthContextType | undefined>(undefined);

interface VendorAuthProviderProps {
  children: ReactNode;
}

export const VendorAuthProvider: React.FC<VendorAuthProviderProps> = ({ children }) => {
  const [vendor, setVendor] = useState<VendorSession | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start with true to prevent redirects during session restoration
  const [hasShownWelcome, setHasShownWelcome] = useState(false); // Track if welcome message has been shown
  const welcomeShownRef = useRef(false); // Ref to track if welcome was shown in current session

  const isAuthenticated = !!vendor;

  // Load vendor session from localStorage on mount
  useEffect(() => {
    const restoreSession = async () => {
      const savedVendor = localStorage.getItem('vendorSession');
      const accessToken = localStorage.getItem('vendorAccessToken');
      
      if (savedVendor && accessToken) {
        try {
          const parsedVendor = JSON.parse(savedVendor);
          
          // Validate the access token by making a test API call
          // For now, we'll trust the token if it exists and has the right format
          // In a production app, you might want to validate it with the backend
          if (accessToken && accessToken.length > 0) {
            setVendor(parsedVendor);
          } else {
            // Token is invalid, clear everything
            localStorage.removeItem('vendorSession');
            localStorage.removeItem('vendorAccessToken');
            localStorage.removeItem('vendorRefreshToken');
          }
        } catch (error) {
          console.error('Error parsing saved vendor session:', error);
          localStorage.removeItem('vendorSession');
          localStorage.removeItem('vendorAccessToken');
          localStorage.removeItem('vendorRefreshToken');
        }
      }
      
      // Session restoration complete
      setIsLoading(false);
    };

    restoreSession();
  }, []);

  // Save vendor session to localStorage whenever it changes
  useEffect(() => {
    if (vendor) {
      localStorage.setItem('vendorSession', JSON.stringify(vendor));
    } else {
      localStorage.removeItem('vendorSession');
    }
  }, [vendor]);

  const validateToken = useCallback(async (token: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await vendorAuthService.validateToken(token);
      
      if (response.success) {
        // Store the onboarding token for use in setPassword
        localStorage.setItem('vendorOnboardingToken', token);
        
        // Verify the token was stored successfully
        const storedToken = localStorage.getItem('vendorOnboardingToken');
        if (storedToken !== token) {
          console.warn('Token storage verification failed, retrying...');
          localStorage.setItem('vendorOnboardingToken', token);
        }
        
        // Only show welcome message once per session using both state and ref
        if (!hasShownWelcome && !welcomeShownRef.current) {
          toast.success('Welcome! You can now continue with your onboarding.');
          setHasShownWelcome(true);
          welcomeShownRef.current = true;
        }
        setVendor(response.vendor);
        return true;
      } else {
        toast.error('Invalid or expired token. Please contact support.');
        return false;
      }
    } catch (error: any) {
      console.error('Token validation error:', error);
      
      // Handle specific error messages from backend
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to validate token. Please try again.';
      
      toast.error(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [hasShownWelcome]);

  const setPassword = useCallback(async (password: string): Promise<boolean> => {
    if (!vendor) {
      toast.error('No vendor session found. Please try again.');
      return false;
    }

    setIsLoading(true);
    
    try {
      // Wait a bit to ensure localStorage is synchronized
      // This prevents race conditions where the token isn't available yet
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Get the onboarding token from localStorage
      const onboardingToken = localStorage.getItem('vendorOnboardingToken');
      if (!onboardingToken) {
        toast.error('No onboarding token found. Please restart the onboarding process.');
        setIsLoading(false);
        return false;
      }

      const response = await vendorAuthService.setPassword(vendor.vendorId, password, onboardingToken);
      
      if (response.success) {
        // Clear the onboarding token since it's been consumed
        localStorage.removeItem('vendorOnboardingToken');
        
        // Update vendor session with new onboarding step and login tokens
        if (response.vendor && response.accessToken && response.refreshToken) {
          setVendor({
            ...response.vendor,
            needsPasswordSetup: false
          });
          // Store tokens for future use
          localStorage.setItem('vendorAccessToken', response.accessToken);
          localStorage.setItem('vendorRefreshToken', response.refreshToken);
        } else {
          // Fallback to updating existing session
          setVendor(prev => prev ? { ...prev, onboardingStep: 1, needsPasswordSetup: false } : null);
        }
        // Don't show toast here - let the component handle the success message
        return true;
      } else {
        toast.error('Failed to set password. Please try again.');
        return false;
      }
    } catch (error: any) {
      console.error('Set password error:', error);
      
      // Handle specific error messages from backend
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to set password. Please try again.';
      
      toast.error(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [vendor]);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await vendorAuthService.login(email, password);
      
      if (response.success) {
        setVendor({
          ...response.vendor,
          needsPasswordSetup: false
        });
        // Store tokens
        localStorage.setItem('vendorAccessToken', response.accessToken);
        localStorage.setItem('vendorRefreshToken', response.refreshToken);
        toast.success('Login successful!');
        return true;
      } else {
        toast.error('Login failed. Please check your credentials.');
        return false;
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle specific error messages from backend
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Login failed. Please try again.';
      
      toast.error(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = () => {
    setVendor(null);
    setHasShownWelcome(false); // Reset welcome flag for next session
    welcomeShownRef.current = false; // Reset ref for next session
    localStorage.removeItem('vendorSession');
    localStorage.removeItem('vendorAccessToken');
    localStorage.removeItem('vendorRefreshToken');
    toast.info('You have been logged out.');
  };

  const value: VendorAuthContextType = {
    vendor,
    isAuthenticated,
    isLoading,
    validateToken,
    setPassword,
    login,
    logout,
  };

  return (
    <VendorAuthContext.Provider value={value}>
      {children}
    </VendorAuthContext.Provider>
  );
};

export const useVendorAuth = (): VendorAuthContextType => {
  const context = useContext(VendorAuthContext);
  if (context === undefined) {
    throw new Error('useVendorAuth must be used within a VendorAuthProvider');
  }
  return context;
};
